import { useEffect, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { agencyService, AgencyPayoutItem } from '../../services/agency.service';
import { toast } from 'react-hot-toast';
import { DataTable, DataTablePagination } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { PayoutMethodModal } from '@/components/affiliate/PayoutMethodModal';
import { PaymentMethodData } from '@/components/affiliate/PaymentInfoForm';
import { isPayoutMethodRequiredError } from '@/services/paymentMethods.service';
import { useAgencyAuth } from '@/contexts/AgencyAuthContext';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const columns: ColumnDef<AgencyPayoutItem>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Date',
    cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
    meta: { width: 'w-[18%]' },
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    cell: ({ row }) => formatCurrency(row.original.amount),
    meta: { width: 'w-[18%]' },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.original.status === 'PENDING'
            ? 'bg-brand-100 text-brand-700'
            : row.original.status === 'PAID'
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
        }`}
      >
        {row.original.status}
      </span>
    ),
    meta: { width: 'w-[18%]' },
  },
  {
    id: 'method',
    header: 'Method',
    cell: ({ row }) => row.original.paymentDetails?.paymentMethodName || '—',
    meta: { width: 'w-[22%]' },
  },
  {
    id: 'reference',
    header: 'Reference',
    cell: ({ row }) => row.original.referenceNumber || '—',
    meta: { width: 'w-[24%]' },
  },
];

export function AgencyPayouts() {
  const [statusLoading, setStatusLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [available, setAvailable] = useState(0);
  const [pending, setPending] = useState(0);
  const [received, setReceived] = useState(0);

  const [history, setHistory] = useState<AgencyPayoutItem[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Inline add-method form, opened when the server says PAYOUT_METHOD_REQUIRED.
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [retryAfterMethod, setRetryAfterMethod] = useState(false);
  const { profile, updateProfile } = useAgencyAuth();

  const savedMethodsAsFormData = (): PaymentMethodData[] =>
    (profile?.paymentMethods ?? []).map((pm) => ({
      paymentMethodId: pm.paymentMethodId,
      fieldValues: Object.entries(pm.fieldData).map(([fieldName, value]) => ({
        fieldName,
        value: String(value),
      })),
    }));

  const fetchStatus = async () => {
    try {
      setStatusLoading(true);
      const s = await agencyService.getPayoutStatus();
      setAvailable(s.availableAmount);
      setPending(s.pendingAmount);
      setReceived(s.paidAmount);
    } catch (e) {
      toast.error('Failed to load payout status');
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchHistory = async (p: number = page, size: number = pageSize) => {
    try {
      setHistoryLoading(true);
      const res = await agencyService.getPayoutHistory(p, size);
      setHistory(res.items);
      setTotalPages(res.pagination.totalPages);
      setTotal(res.pagination.total);
      setPage(res.pagination.page);
    } catch (e) {
      toast.error('Failed to load payout history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleConfirmRequest = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await agencyService.requestAgencyCommission();
      toast.success('Payout request submitted');
      setShowConfirm(false);
      // Refresh status and history
      fetchStatus();
      fetchHistory(1);
    } catch (e: any) {
      const data = e?.response?.data;
      if (isPayoutMethodRequiredError(data)) {
        // The one place payout details are required: collect them inline and
        // resubmit, no trip to Settings.
        setShowConfirm(false);
        setRetryAfterMethod(true);
        setShowMethodModal(true);
        return;
      }
      toast.error(data?.message || 'Failed to request payout');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMethodSaved = async () => {
    setShowMethodModal(false);
    if (retryAfterMethod) {
      setRetryAfterMethod(false);
      await handleConfirmRequest();
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchHistory(1);
  }, []);

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Payouts</h1>
          <p className="text-muted-foreground">Manage payout requests and track payment history</p>
        </div>
        <button
          onClick={() => setShowConfirm(true)}
          disabled={statusLoading || available < 10}
          className="px-4 py-2 rounded-lg text-white disabled:opacity-50 bg-brand-600 hover:bg-brand-700 transition-colors"
        >
          Request Payout
        </button>
      </div>

      {/* Money is waiting and there is nowhere to send it. A nudge, not a
          wall — the request itself is what requires a method. */}
      {!statusLoading && available > 0 && (profile?.paymentMethods?.length ?? 0) === 0 && (
        <div
          className="flex flex-col gap-3 rounded-xl border border-brand-600/20 bg-brand-600/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
          role="status"
          data-testid="agency-payout-method-banner"
        >
          <div>
            <p className="text-sm font-medium text-foreground">
              You've earned {formatCurrency(available)}. Tell us where to send it.
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Add one payout method and you're ready to request.
            </p>
          </div>
          <Button size="sm" onClick={() => { setRetryAfterMethod(false); setShowMethodModal(true); }}>
            Add payout method
          </Button>
        </div>
      )}

      <PayoutMethodModal
        isOpen={showMethodModal}
        onClose={() => { setShowMethodModal(false); setRetryAfterMethod(false); }}
        initialData={savedMethodsAsFormData()}
        description="Where Reveshare sends your commission. One method is enough."
        onSave={async (methods) => {
          const updated = await agencyService.updatePaymentMethods(methods);
          updateProfile(updated);
        }}
        onSaved={handleMethodSaved}
        saveLabel={retryAfterMethod ? 'Save and request payout' : 'Save payout method'}
      />

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg shadow p-4 border-l-4 border-brand-500">
          <p className="text-sm text-muted-foreground">Available</p>
          <p className="text-2xl font-bold text-brand-700">{statusLoading ? '—' : formatCurrency(available)}</p>
        </div>
        <div className="bg-card rounded-lg shadow p-4 border-l-4 border-brand-300">
          <p className="text-sm text-muted-foreground">Requested (Pending)</p>
          <p className="text-2xl font-bold text-foreground">{statusLoading ? '—' : formatCurrency(pending)}</p>
        </div>
        <div className="bg-card rounded-lg shadow p-4 border-l-4 border-green-400">
          <p className="text-sm text-muted-foreground">Received</p>
          <p className="text-2xl font-bold text-foreground">{statusLoading ? '—' : formatCurrency(received)}</p>
        </div>
      </div>

      {/* History table */}
      <div className="rounded-xl border border-border bg-card shadow-sm">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Payout History</h2>
        </div>
        <DataTable
          columns={columns}
          data={history}
          isLoading={historyLoading}
          emptyText="No payout history"
        />
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={(p) => fetchHistory(p)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            fetchHistory(1, size);
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          noun="payouts"
        />
      </div>

      {/* Confirm Request Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-xl w-full max-w-md shadow-lg">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Confirm Payout Request</h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-muted-foreground">
                You are about to request a payout of
                <span className="font-semibold text-brand-700"> {formatCurrency(available)}</span>.
              </p>
              <p className="text-xs text-muted-foreground">Only unrequested commission will be included.</p>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:bg-accent"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <Button
                onClick={handleConfirmRequest}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Requesting...' : 'Confirm Request'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
